import React, { MouseEventHandler, ReactNode, useRef } from 'react';
import {
	CSSObject,
	ListItem,
	ListItemButton as MuiListItemButton,
	ListItemIcon as MuiListItemIcon,
	ListItemText as MuiListItemText,
	ListItemTextProps,
	styled,
	Theme,
} from '@mui/material';
import { Link, NavLink } from 'react-router-dom';
import { NAV_COLORS } from 'shared/_components/app-layout/nav/styles';
import { NAV_BAR_LIST_ITEM_HEIGHT } from 'shared/_components/app-layout/constants';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';

function getListItemSx(theme: Theme): CSSObject {
	return {
		display: 'block',
		paddingLeft: 1,
		color: NAV_COLORS.text,
		textTransform: 'uppercase',
		transition: 'background-color 0.2s, color 0.2s',
		'&:hover, &.active': {
			backgroundColor: NAV_COLORS.bgHover,
			color: NAV_COLORS.textActive,
		},
		// Amber bar on the right edge of the active item
		'&.active': {
			boxShadow: `inset -3px 0 0 ${NAV_COLORS.accent}`,
		},
	};
}

const ListItemButton = styled(MuiListItemButton, {
	shouldForwardProp: (prop) => !['expanded', 'level'].includes(prop as string),
})<{ expanded: boolean; level?: number }>(({ theme, expanded, level }) => ({
	minHeight: NAV_BAR_LIST_ITEM_HEIGHT,
	justifyContent: expanded ? 'initial' : 'center',
	paddingInlineStart: expanded ? theme.spacing(5 + 4 * (level ?? 0)) : theme.spacing(1),
	paddingInlineEnd: expanded ? theme.spacing(5) : theme.spacing(1),
	'&:hover': { backgroundColor: 'transparent' },
}));

const ListItemIcon = styled(MuiListItemIcon, {
	shouldForwardProp: (prop) => prop !== 'expanded',
})<{ expanded: boolean }>(({ expanded, theme }) => ({
	color: 'inherit',
	minWidth: 0,
	marginRight: expanded ? theme.spacing(3) : 0,
	justifyContent: 'center',
}));

function ListItemText({ expanded, ...props }: ListItemTextProps & { expanded: boolean }) {
	return (
		<MuiListItemText
			{...props}
			sx={{ opacity: expanded ? 1 : 0 }}
			slotProps={{
				primary: {
					sx: {
						// dense items render body2, which has its own color: inherit the item color (hover/active)
						color: 'inherit',
						fontSize: '1rem',
						textOverflow: 'ellipsis',
						overflow: 'hidden',
						whiteSpace: 'nowrap',
					},
				},
			}}
		/>
	);
}

type Props = {
	icon?: ReactNode;
	expanded: boolean;
	text: ReactNode;
	level?: number;
};

type NavListItemProps = Props & {
	onClick?: MouseEventHandler<HTMLDivElement>;
	isGroupExpanded?: boolean;
};

export function NavListItem(props: NavListItemProps) {
	const { icon, expanded, onClick, text, isGroupExpanded, level } = props;

	return (
		<ListItem disablePadding sx={(theme) => getListItemSx(theme)}>
			<ListItemButton expanded={expanded} onClick={onClick} level={level}>
				{icon && <ListItemIcon expanded={expanded}>{icon}</ListItemIcon>}
				<ListItemText primary={text} expanded={expanded || !icon} />
				{isGroupExpanded === true && <ExpandLess />}
				{isGroupExpanded === false && <ExpandMore />}
			</ListItemButton>
		</ListItem>
	);
}

type NavLinkListItemProps = Props & {
	to: string;
	isNavLink?: boolean;
};

export function NavLinkListItem(props: NavLinkListItemProps) {
	const { icon, expanded, text, to, isNavLink = true, level } = props;
	const ref = useRef(null);

	return (
		<ListItem
			ref={ref}
			component={isNavLink ? NavLink : Link}
			to={to}
			disablePadding
			sx={(theme) => ({
				...getListItemSx(theme),
			})}
			dense
		>
			<ListItemButton dense expanded={expanded} level={level}>
				{icon && <ListItemIcon expanded={expanded}>{icon}</ListItemIcon>}
				<ListItemText primary={text} expanded={expanded || !icon} />
			</ListItemButton>
		</ListItem>
	);
}
